// ipad-pwa/src/lib/storage.js
// =====================================================================
// Persistent storage for the iPad PWA. Backed by IndexedDB for stronger
// iOS persistence guarantees than localStorage. Sync get/set/remove API
// preserved so existing callers don't have to become async — reads go
// through an in-memory cache that's hydrated from IDB on boot, writes
// update the cache synchronously and fire-and-forget the IDB persist.
//
// One-time migration: hydrateCache() reads any leftover values from the
// previous localStorage backend (key prefix 'iptbc.ipad.'), copies them
// into IDB, and removes them from localStorage. iPads paired pre-IDB
// upgrade transparently without needing to re-pair.
// =====================================================================

import { idbGet, idbSet, idbDelete } from './idbStorage.js';

const LS_PREFIX = 'iptbc.ipad.'; // legacy localStorage prefix
const KEYS = ['device', 'sealed', 'activeSession'];

const cache = {
  device: null,
  sealed: null,
  activeSession: null,
};

let _hydrated = false;
let _hydratePromise = null;

/**
 * Read any pre-IDB localStorage values, copy into IDB if IDB is empty,
 * remove from localStorage, populate the in-memory cache. Idempotent +
 * safe to call multiple times (subsequent calls return the prior promise).
 *
 * Must be awaited before any sync getter is meaningful. The App's
 * boot flow awaits this via auth.bootstrap() before flipping the
 * 'booting' state to false.
 */
export function hydrateCache() {
  if (_hydratePromise) return _hydratePromise;
  _hydratePromise = (async () => {
    for (const key of KEYS) {
      let v = await idbGet(key);
      if (v == null) {
        // One-time migration from the localStorage backend.
        try {
          const raw = localStorage.getItem(LS_PREFIX + key);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed != null) {
              v = parsed;
              await idbSet(key, parsed);
              localStorage.removeItem(LS_PREFIX + key);
              console.info('[storage] migrated', key, 'from localStorage to IDB');
            }
          }
        } catch { /* swallow */ }
      }
      cache[key] = v;
    }
    _hydrated = true;
  })();
  return _hydratePromise;
}

function setKey(key, value) {
  cache[key] = value;
  idbSet(key, value).catch((err) => console.warn('[storage] set failed', key, err));
}
function delKey(key) {
  cache[key] = null;
  idbDelete(key).catch((err) => console.warn('[storage] delete failed', key, err));
}

/* ---------- device pairing (legacy / Phase-1 path) ---------- */
// { token, device: { id, name, location } }
export const getDevice = () => cache.device;
export const setDevice = (rec) => setKey('device', rec);
export const clearDevice = () => delKey('device');

/* ---------- active session ---------- */
// { id, client_uuid, cathay_id_entered, signin_at_utc, signin_rounded_hour }
export const getActiveSession = () => cache.activeSession;
export const setActiveSession = (s) => setKey('activeSession', s);
export const clearActiveSession = () => delKey('activeSession');

/* ---------- Phase-2 sealed bundle ----------
   Sensitive parts (device_token, signing_secret) live ONLY inside
   ciphertext_b64, AES-GCM-encrypted with half_a XOR half_b. */
export const getSealedBundle = () => cache.sealed;
export const setSealedBundle = (b) => setKey('sealed', b);
export const clearSealedBundle = () => delKey('sealed');

// Diagnostic helper — exposed for the pairing screen's debug indicator.
export function isHydrated() { return _hydrated; }
